import React, { useEffect, useMemo, useRef, useState } from "react";
import BpkdPkdBrowser from "../components/BpkdPkdBrowser";
import { useApi } from "../hooks/useApi";

// ---------------------------------------------------------------------------
// Header background colors (one per logical column group)
// ---------------------------------------------------------------------------
// Header background colors only — text is forced black globally via `thBase`.
const H = {
  hour: "bg-blue-400",
  reserve: "bg-blue-400",
  cyan: "bg-cyan-300",
  gray: "bg-gray-300",
  rdsp: "bg-green-300",
  red: "bg-red-400",
  blue: "bg-blue-400",
  yellow: "bg-yellow-300",
  indigo: "bg-indigo-300",
  zrb: "bg-green-500",
  batt: "bg-purple-400",
  battDis: "bg-fuchsia-400",
  dark: "bg-slate-200",
  white: "bg-white",
  // Price block (last seven columns)
  rce: "bg-teal-300",
  ceb: "bg-sky-300",
  cen: "bg-orange-300",
  rceEur: "bg-teal-500",
  cebEur: "bg-sky-500",
  cenEur: "bg-orange-500",
};

// ---------------------------------------------------------------------------
// Cell background colors (one per logical column group)
// ---------------------------------------------------------------------------
const C = {
  hour: "bg-white text-black font-semibold",
  reserve: "bg-pink-200 text-pink-900",
  cyan: "bg-sky-50 text-blue-700",
  gray: "bg-gray-100 text-slate-700",
  temp: "bg-cyan-100 text-slate-800",
  rdsp: "bg-green-50 text-slate-800",
  red: "bg-red-100 text-red-900",
  blue: "bg-blue-100 text-blue-900",
  yellow: "bg-yellow-100 text-yellow-900",
  indigo: "bg-indigo-100 text-indigo-900",
  zrb: "bg-green-100 text-green-900",
  batt: "bg-purple-100 text-purple-900",
  battDis: "bg-fuchsia-100 text-fuchsia-900",
  // Price block (last seven columns)
  rce: "bg-teal-100 text-teal-900",
  ceb: "bg-sky-100 text-sky-900",
  cen: "bg-orange-100 text-orange-900",
  rceEur: "bg-teal-100 text-teal-900",
  cebEur: "bg-sky-100 text-sky-900",
  cenEur: "bg-orange-100 text-orange-900",
};

// ---------------------------------------------------------------------------
// Full column definition (exact order / names / color-coding from screenshots)
// ---------------------------------------------------------------------------
const COLUMNS = [
  { label: "Hour", h: H.white, c: C.hour, isHour: true },

  // Reserve / outages block
  { label: "Reserve#2", h: H.cyan, c: C.cyan, field: "reserve2", heat: true },
  { label: "ReserveLast", h: H.cyan, c: C.cyan, field: "reserveLast", heat: true },
  { label: "SUB", h: H.cyan, c: "bg-sky-50", field: "sub", sign: "blueRed", topField: "reserveRev", minW: 74 },
  { label: "SUBmod", h: H.cyan, c: "bg-sky-50", field: "subMod", sign: "blueRed" },
  { label: "Outages#2", h: H.gray, c: C.gray, field: "outages2" },
  { label: "Last", h: H.gray, c: C.gray, field: "outagesLast" },
  { label: "SUB", h: H.gray, c: "bg-gray-100", field: "outagesSub", sign: "blackRed", topField: "outagesRev", topSign: "blueRed", minW: 74 },
  { label: "Temp", h: H.cyan, c: C.temp, field: "temp" },

  { label: "Hour", h: H.white, c: C.hour, isHour: true },

  // Load block (red)
  { label: "RDSP", h: H.rdsp, c: "bg-green-50", field: "rdsp", sign: "redBlue", topField: "rdspRev", minW: 74 },
  { label: "Load#2", h: H.red, c: "bg-red-100 text-gray-900", field: "load2" },
  { label: "SUB", h: H.red, c: "bg-red-100", field: "loadSub", sign: "redBlue", topField: "loadRev", minW: 74 },
  { label: "Last", h: H.red, c: "bg-red-100 text-gray-900", field: "loadLast" },
  { label: "RT-Last", h: H.red, c: "bg-red-100", field: "rtLast", sign: "redBlue", subField: "battLast", minW: 74 },
  { label: "RealTime", h: H.dark, c: "bg-red-100 text-gray-900", badge: true, field: "realtime" },
  { label: "AT-Last", h: H.red, c: "bg-red-100", field: "atLast", sign: "redBlue" },
  { label: "Actual", h: H.red, c: "bg-red-100 text-gray-900", field: "actual" },
  { label: "Meteo", h: H.red, c: "bg-red-100 text-gray-900", field: "meteo" },

  // BATT block (purple) — tot_jgm_char_pow
  { label: "BATT#2", h: H.batt, c: "bg-purple-100 text-gray-900", field: "batt2" },
  { label: "Last", h: H.batt, c: "bg-purple-100 text-gray-900", field: "battLast" },
  { label: "SUB", h: H.batt, c: "bg-purple-100", field: "battSub", sign: "redBlue" },

  // BATTDis block (fuchsia) — gen_jgm_zak_1
  { label: "BATTDis#2", h: H.battDis, c: "bg-fuchsia-100 text-gray-900", field: "battDis2" },
  { label: "Last", h: H.battDis, c: "bg-fuchsia-100 text-gray-900", field: "battDisLast" },
  { label: "SUB", h: H.battDis, c: "bg-fuchsia-100", field: "battDisSub", sign: "blueRed" },

  { label: "Hour", h: H.white, c: C.hour, isHour: true },

  // Wind block (blue)
  { label: "Wind#2", h: H.blue, c: "bg-blue-100 text-gray-900", field: "wind2" },
  { label: "SUB", h: H.blue, c: "bg-blue-100", field: "windSub", sign: "blueRed", topField: "windRev", minW: 74 },
  { label: "Last", h: H.blue, c: "bg-blue-100 text-gray-900", field: "windLast" },
  { label: "RT-Last", h: H.blue, c: "bg-blue-100", field: "windRtLast", sign: "blueRed" },
  { label: "RealTime", h: H.dark, c: "bg-blue-100 text-gray-900", badge: true, field: "windRealtime" },
  { label: "AT-Last", h: H.blue, c: "bg-blue-100", field: "windAtLast", sign: "blueRed" },
  { label: "Actual", h: H.blue, c: "bg-blue-100 text-gray-900", field: "windActual" },
  { label: "Meteo", h: H.blue, c: "bg-blue-100 text-gray-900", field: "windMeteo" },

  // Solar block (yellow) — same coding as Wind
  { label: "Solar#2", h: H.yellow, c: "bg-yellow-100 text-gray-900", field: "solar2" },
  { label: "SUB", h: H.yellow, c: "bg-yellow-100", field: "solarSub", sign: "blueRed", topField: "solarRev", minW: 74 },
  { label: "Last", h: H.yellow, c: "bg-yellow-100 text-gray-900", field: "solarLast" },
  { label: "RT-Last", h: H.yellow, c: "bg-yellow-100", field: "solarRtLast", sign: "blueRed" },
  { label: "RealTime", h: H.dark, c: "bg-yellow-100 text-gray-900", badge: true, field: "solarRealtime" },
  { label: "AT-Last", h: H.yellow, c: "bg-yellow-100", field: "solarAtLast", sign: "blueRed" },
  { label: "Actual", h: H.yellow, c: "bg-yellow-100 text-gray-900", field: "solarActual" },
  { label: "Meteo", h: H.yellow, c: "bg-yellow-100 text-gray-900", field: "solarMeteo" },

  // WM block (indigo / lavender)
  { label: "WM#2", h: H.indigo, c: "bg-indigo-100 text-gray-900", field: "wm2" },
  { label: "Last", h: H.indigo, c: "bg-indigo-100 text-gray-900", field: "wmLast" },
  { label: "SUB", h: H.indigo, c: "bg-indigo-100", field: "wmSub", sign: "blueRed", topField: "wmRev", minW: 74 },

  { label: "Hour", h: H.white, c: C.hour, isHour: true },

  // ZRB block (green)
  { label: "ZRB#2", h: H.zrb, c: "bg-green-100 text-gray-900", field: "zrb2" },
  { label: "ZRBLast", h: H.zrb, c: "bg-green-100 text-gray-900", field: "zrbLast" },
  { label: "SUB", h: H.zrb, c: "bg-green-100", field: "zrbSub", sign: "redBlue", topField: "zrbRev", subField: "rdsp", minW: 74 },
  { label: "Njex#2", h: H.zrb, c: "bg-green-100 text-gray-900", field: "njex2" },
  { label: "NjexLast", h: H.zrb, c: "bg-green-100 text-gray-900", field: "njexLast" },

  // SK block
  { label: "Live", h: H.blue, c: "bg-blue-100", field: "live", sign: "redBlue" },
  { label: "SK-1", h: H.red, c: "bg-red-100", field: "sk1", sign: "redBlue" },
  { label: "SK-1+SUB+Live", h: H.red, c: "bg-red-100 text-gray-900", field: "skSubLive" },
  { label: "SK-1+SUB+AT", h: H.red, c: "bg-red-100 text-gray-900", field: "skSubAt" },
  { label: "SK-0", h: H.red, c: "bg-red-100", field: "sk0", sign: "redBlue" },
  { label: "SK", h: H.red, c: "bg-red-100", field: "sk", sign: "redBlue" },

  // Price block (last seven columns) — 2-decimal precision
  { label: "RCE", h: H.rce, c: "bg-teal-100 text-gray-900", field: "rce", dec: 2 },
  { label: "CEB", h: H.ceb, c: "bg-sky-100 text-gray-900", field: "ceb", dec: 2, subField: "cebFcst", minW: 74 },
  { label: "CEN", h: H.cen, c: "bg-orange-100 text-gray-900", field: "cen", dec: 2, subField: "cenFcst", minW: 74 },
  { label: "RCE EUR", h: H.rceEur, c: "bg-teal-100 text-gray-900", field: "rceEur", dec: 2 },
  { label: "CEB EUR", h: H.cebEur, c: "bg-sky-100 text-gray-900", field: "cebEur", dec: 2 },
  { label: "CEN EUR", h: H.cenEur, c: "bg-orange-100 text-gray-900", field: "cenEur", dec: 2 },

  { label: "Hour", h: H.white, c: C.hour, isHour: true },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i + 1);
const QUARTERS = [1, 2, 3, 4];

// Display a numeric cell value, blank if absent. With `dec`, force that many
// decimals (price columns); otherwise ints as-is, fractional to one decimal.
const fmtVal = (v, dec) => {
  if (v === null || v === undefined || v === "") return "";
  if (!Number.isFinite(v)) return "";
  if (dec != null) return v.toFixed(dec);
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
};

// Resolve a YYYY-MM-DD date in Europe/Warsaw, offset by `offsetDays`.
const warsawDate = (offsetDays = 0) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Warsaw",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (t) => Number(parts.find((p) => p.type === t).value);
  const dt = new Date(Date.UTC(get("year"), get("month") - 1, get("day")));
  dt.setUTCDate(dt.getUTCDate() + offsetDays);
  return dt.toISOString().slice(0, 10);
};

// Value heatmap for Reserve#2 / ReserveLast: 3-stop gradient — sweet pink
// (lowest) -> yellow (mid) -> green (highest). Bright in light mode (dark text);
// dark in dark mode (light text), since dark mode forces light cell text.
const HEAT_LIGHT = {
  lo: [249, 168, 212], // pink-300
  mid: [253, 224, 71], // yellow-300
  hi: [74, 222, 128], // green-400
  text: "#111827",
};
const HEAT_DARK = {
  lo: [157, 23, 77], // pink-800
  mid: [161, 98, 7], // yellow-700
  hi: [22, 101, 52], // green-800
  text: "#e8edf5",
};
const HEAT_K = 12; // steepness: higher = bigger colour change per value change
const heatStyle = (value, min, max, dark) => {
  let t = max > min ? Math.min(1, Math.max(0, (value - min) / (max - min))) : 0;
  // Sigmoid contrast: steepen the gradient so small value changes near the
  // middle produce larger colour changes (extremes saturate faster).
  const sig = (x) => 1 / (1 + Math.exp(-HEAT_K * (x - 0.5)));
  const s0 = sig(0);
  t = (sig(t) - s0) / (sig(1) - s0);
  const p = dark ? HEAT_DARK : HEAT_LIGHT;
  const [a, b, tt] = t < 0.5 ? [p.lo, p.mid, t / 0.5] : [p.mid, p.hi, (t - 0.5) / 0.5];
  const c = a.map((x, i) => Math.round(x + (b[i] - x) * tt));
  return { backgroundColor: `rgb(${c[0]}, ${c[1]}, ${c[2]})`, color: p.text };
};

// Sign-based text color schemes (classes carry their own dark-mode overrides).
// blue/red/black map to text-blue-600 / text-red-600 / text-gray-900.
const SIGN_SCHEMES = {
  blueRed: (v) => (v < 0 ? "text-red-600" : "text-blue-600"), // +blue / -red
  blackRed: (v) => (v < 0 ? "text-red-600" : "text-gray-900"), // +black / -red
  redBlue: (v) => (v < 0 ? "text-blue-600" : "text-red-600"), // +red / -blue
};

// Current clock hour in Europe/Warsaw, 0..23.
const warsawHourNow = () =>
  Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Warsaw",
      hour: "2-digit",
      hourCycle: "h23",
    }).format(new Date())
  );

// Current minutes-since-midnight in Europe/Warsaw (0..1439).
const warsawNowMinutes = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Warsaw",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const get = (t) => Number(parts.find((p) => p.type === t).value);
  return get("hour") * 60 + get("minute");
};

// Gate closure lead time: a product closes 1h before its delivery start.
const GATE_LEAD_MIN = 60;

// Next tradeable hour (1..24) and quarter (its hour 1..24 + quarter 1..4) for a
// delivery date: the soonest product whose gate (delivery start - 1h) is still
// in the future. null when nothing on that date is still open (e.g. late at
// night, today's products are all past, but tomorrow's are not).
const nextTradedFor = (deliveryDate) => {
  if (!deliveryDate) return { hour: null, qHour: null, qIdx: null };
  const threshold = warsawNowMinutes() + GATE_LEAD_MIN;
  const diffDays = Math.round(
    (Date.parse(`${deliveryDate}T00:00:00Z`) - Date.parse(`${warsawDate(0)}T00:00:00Z`)) /
      86400000
  );
  const offset = diffDays * 1440; // minutes from "now's midnight" to the date's midnight
  let nh = null;
  for (let h = 1; h <= 24; h += 1) {
    if (offset + (h - 1) * 60 > threshold) {
      nh = h;
      break;
    }
  }
  let ng = null;
  for (let g = 1; g <= 96; g += 1) {
    if (offset + (g - 1) * 15 > threshold) {
      ng = g;
      break;
    }
  }
  return {
    hour: nh,
    qHour: ng ? Math.floor((ng - 1) / 4) + 1 : null,
    qIdx: ng ? ((ng - 1) % 4) + 1 : null,
  };
};

const thBase =
  "px-2 py-1 text-[11px] font-semibold text-center whitespace-nowrap border border-black text-black";
const tdBase =
  "relative px-2 py-1 text-[11px] whitespace-nowrap border border-black min-w-[58px] transition group-hover:brightness-90";

// Content rendered inside an "Hour" column cell.
const HourLabel = ({ hour, quarter }) => {
  if (!quarter) return <>{`h${hour}`}</>;
  const globalIdx = (hour - 1) * 4 + quarter;
  return (
    <>
      <span className="text-black">{`q${quarter}h${hour}`}</span>
      <span className="text-red-600 font-bold ml-0.5">-{globalIdx}</span>
    </>
  );
};

// A single body row (an hour row, or one of its quarter rows).
// `values` carries the data columns for this row, e.g. { reserve2, reserveLast, sub }.
const DataRow = ({
  hour,
  quarter,
  onClick,
  expanded,
  values,
  isCurrent,
  showHighlights,
  heatRange,
  darkMode,
  nextTraded,
}) => {
  const isQuarter = Boolean(quarter);
  // This row is the next tradeable hour, or the next tradeable quarter.
  const nextMark =
    !isQuarter && hour === nextTraded?.hour
      ? "next-hour"
      : isQuarter && hour === nextTraded?.qHour && quarter === nextTraded?.qIdx
      ? "next-qtr"
      : "";
  // On quarter rows only, CEN equals RCE or CEB; highlight whichever one it
  // matches (not CEN itself, which always matches). RCE match brightens; the
  // CEB match uses a distinct colour. Never on the hourly average row.
  const priceMatchClass = {};
  if (isQuarter && values) {
    const { cen, rce, ceb } = values;
    if (cen != null && rce != null && cen === rce) {
      priceMatchClass.rce = "price-match";
    }
    if (cen != null && ceb != null && cen === ceb) {
      priceMatchClass.ceb = "ceb-match";
    }
  }
  // SK gold: a quarter cell is light gold when SK-0 and SK have opposite signs;
  // the hour cell is full gold when any of its quarters qualifies.
  const skFlip = (o) =>
    o && o.sk0 != null && o.sk != null && o.sk0 * o.sk < 0;
  const skGold = isQuarter
    ? skFlip(values)
      ? "sk-gold-light"
      : ""
    : values?.quarters?.some(skFlip)
    ? "sk-gold"
    : "";
  return (
    <tr
      onClick={onClick}
      className={`group ${onClick ? "cursor-pointer" : ""}`}
    >
      {COLUMNS.map((col, idx) => {
        const heatVal = col.heat ? values?.[col.field] : null;
        const heat =
          col.heat && typeof heatVal === "number" && Number.isFinite(heatVal) && heatRange
            ? heatStyle(heatVal, heatRange.min, heatRange.max, darkMode)
            : null;
        // Sign-based text color (blue/red/black per scheme), class-based for dark mode.
        const signVal = col.sign ? values?.[col.field] : null;
        const signClass =
          col.sign && typeof signVal === "number" ? SIGN_SCHEMES[col.sign](signVal) : "";
        return (
        <td
          key={idx}
          style={{
            ...(col.minW ? { minWidth: `${col.minW}px` } : {}),
            // heat cell -> colored bg + contrast text; heat column w/o value -> black text.
            ...(heat ? heat : col.heat ? { color: "#000000" } : {}),
          }}
          className={`${tdBase} ${heat ? "" : col.c} ${signClass} ${
            col.heat ? "" : nextMark
          } ${isQuarter ? "brightness-105" : ""} ${
            isCurrent ? "font-bold" : ""
          } ${
            col.isHour ? "min-w-[68px] font-semibold text-center" : "text-left"
          } ${isQuarter && quarter === 1 ? "border-t-2" : ""} ${
            isQuarter && quarter === 4 ? "border-b-2" : ""
          } ${(col.field && priceMatchClass[col.field]) || ""} ${
            col.field === "sk" ? skGold : ""
          } ${
            !isQuarter && col.isHour && expanded ? "ring-1 ring-blue-400" : ""
          }`}
        >
          {col.isHour ? (
            <HourLabel hour={hour} quarter={quarter} />
          ) : col.field ? (
            <>
              {fmtVal(values?.[col.field], col.dec)}
              {col.subField && values?.[col.subField] ? (
                <span
                  className={`absolute bottom-0 right-0.5 text-[9px] leading-none font-semibold ${
                    col.sign ? SIGN_SCHEMES[col.sign](values[col.subField]) : ""
                  }`}
                >
                  {fmtVal(values[col.subField], col.dec)}
                </span>
              ) : null}
              {col.topField && showHighlights && values?.[col.topField] ? (
                <span
                  className={`absolute top-0 right-0.5 text-[9px] leading-none font-semibold ${
                    col.topSign || col.sign
                      ? SIGN_SCHEMES[col.topSign || col.sign](values[col.topField])
                      : values[col.topField] > 0
                      ? "text-red-600"
                      : values[col.topField] < 0
                      ? "text-green-700"
                      : "text-slate-500"
                  }`}
                >
                  {fmtVal(values[col.topField])}
                </span>
              ) : null}
            </>
          ) : (
            ""
          )}
        </td>
        );
      })}
    </tr>
  );
};

const Dashboard = ({ darkMode }) => {
  const { fetchGet, isAuthenticated } = useApi();

  const [mode, setMode] = useState("today");
  const [date, setDate] = useState(() => warsawDate(-1)); // calendar defaults to yesterday
  const [expanded, setExpanded] = useState(() => new Set());
  const [showHighlights, setShowHighlights] = useState(true); // BPKD-revision indices

  const allExpanded = expanded.size === HOURS.length;
  const toggleAllQuarters = () =>
    setExpanded(allExpanded ? new Set() : new Set(HOURS));

  // Uniquely identify the selected 96-row packs (by publication_ts_utc et al.).
  // These feed subsequent API queries as input params.
  const [bpkdSelected, setBpkdSelected] = useState(null); // BPKD_SELECTED ("Last")
  const [pkdSelected, setPkdSelected] = useState(null); // PKD_SELECTED

  // Table data keyed by hour (1..24); refreshed every 5s.
  const [hoursByIndex, setHoursByIndex] = useState({});
  const [latest, setLatest] = useState({}); // field -> newest value (green RealTime badges)
  const [eurPln, setEurPln] = useState(null); // EUR/PLN rate for the date
  const [lastRefresh, setLastRefresh] = useState(null);

  // The business date driving the table: today/tomorrow in Warsaw, or the
  // calendar value when browsing history.
  const resolvedDate =
    mode === "today" ? warsawDate(0) : mode === "tomorrow" ? warsawDate(1) : date;

  // Dashboard hour (1..24) that is "now" — only meaningful when viewing today.
  // Recomputed each render; the 5s poll keeps it fresh as the hour rolls over.
  const currentHour = mode === "today" ? warsawHourNow() + 1 : null;

  // Selected pack ids (stable across auto-refresh object churn).
  const pkdTs = pkdSelected?.publication_ts_utc || "";
  const bpkdTs = bpkdSelected?.publication_ts_utc || "";

  // Value range across all Reserve#2 / ReserveLast cells, for the heatmap.
  const heatRange = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    const acc = (o) => {
      if (!o) return;
      [o.reserve2, o.reserveLast].forEach((v) => {
        if (typeof v === "number" && Number.isFinite(v)) {
          if (v < min) min = v;
          if (v > max) max = v;
        }
      });
    };
    Object.values(hoursByIndex).forEach((h) => {
      acc(h);
      h.quarters?.forEach(acc);
    });
    return max > min ? { min, max } : null;
  }, [hoursByIndex]);

  // Next tradeable hour/quarter for the chosen delivery date (recomputed each
  // 5s refresh, since it depends on the current time).
  const nextTraded = useMemo(
    () => nextTradedFor(resolvedDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolvedDate, lastRefresh]
  );

  useEffect(() => {
    if (!isAuthenticated || !resolvedDate) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        const params = new URLSearchParams({ date: resolvedDate });
        if (pkdTs) params.set("pkd", pkdTs);
        if (bpkdTs) params.set("bpkd", bpkdTs);
        const res = await fetchGet(`/update-dashboard?${params.toString()}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const byHour = {};
        (data.hours || []).forEach((h) => {
          byHour[h.hour] = h;
        });
        setHoursByIndex(byHour);
        setLatest(data.latest || {});
        setEurPln(data.eurPln ?? null);
      } catch (e) {
        // Keep the last good data on a transient failure.
      } finally {
        if (!cancelled) setLastRefresh(new Date().toLocaleTimeString());
      }
    };

    load();
    const id = setInterval(load, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isAuthenticated, resolvedDate, pkdTs, bpkdTs, fetchGet]);

  // Midnight rollover: "Tomorrow" is relative (current day + 1), so at midnight
  // it would advance to a date whose packs aren't published yet (blank table).
  // When the Warsaw day rolls over, flip Tomorrow -> Today so the delivery date
  // being watched stays in view as it becomes the active trading day. Checked
  // on every 5s refresh, so the switch lands within seconds of midnight.
  const lastDayRef = useRef(warsawDate(0));
  useEffect(() => {
    const nowDay = warsawDate(0);
    if (nowDay !== lastDayRef.current) {
      lastDayRef.current = nowDay;
      if (mode === "tomorrow") setMode("today");
    }
  }, [lastRefresh, mode]);

  const toggleHour = (hour) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(hour)) {
        next.delete(hour);
      } else {
        next.add(hour);
      }
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {/* ---------------------------------------------------------------- */}
      {/* Controls above the table                                          */}
      {/* ---------------------------------------------------------------- */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleAllQuarters}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            {allExpanded ? "Collapse Quarters" : "Show Quarters"}
          </button>
          <button
            type="button"
            onClick={() => setShowHighlights((v) => !v)}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            {showHighlights ? "Clear highlight" : "Show highlight"}
          </button>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-6 text-xs text-gray-600">
            <span>Last: {bpkdSelected?.created_at || "—"}</span>
            <span>Pkd: {pkdSelected?.created_at || "—"}</span>
            <span>Last refresh: {lastRefresh || "—"}</span>
            <span>EUR: {eurPln != null ? eurPln.toFixed(4) : "—"}</span>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <input
              type="date"
              value={resolvedDate}
              onChange={(e) => setDate(e.target.value)}
              disabled={mode !== "historical"}
              className={`px-2 py-1 border rounded text-sm ${
                mode === "historical"
                  ? "border-gray-300 text-gray-700 bg-white"
                  : "border-gray-200 text-gray-400 bg-gray-100 cursor-not-allowed"
              }`}
            />
            <label className="flex items-center gap-1 text-sm text-gray-700">
              <input
                type="radio"
                name="mode"
                checked={mode === "historical"}
                onChange={() => {
                  setMode("historical");
                  setDate(warsawDate(-1)); // start browsing from the previous day
                }}
                className="accent-green-600"
              />
              Historical
            </label>
            <label className="flex items-center gap-1 text-sm text-gray-700">
              <input
                type="radio"
                name="mode"
                checked={mode === "today"}
                onChange={() => setMode("today")}
                className="accent-green-600"
              />
              Today
            </label>
            <label className="flex items-center gap-1 text-sm text-gray-700">
              <input
                type="radio"
                name="mode"
                checked={mode === "tomorrow"}
                onChange={() => setMode("tomorrow")}
                className="accent-green-600"
              />
              Tomorrow
            </label>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* The data table (always full width, horizontally scrollable)       */}
      {/* ---------------------------------------------------------------- */}
      <div className="table-scroll relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen overflow-x-auto border-y border-black">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {COLUMNS.map((col, idx) => (
                <th
                  key={idx}
                  style={col.minW ? { minWidth: `${col.minW}px` } : undefined}
                  className={`${thBase} ${col.h}`}
                >
                  {col.badge ? (
                    <div className="flex flex-col items-center leading-tight">
                      <span>{col.label}</span>
                      <span className="mt-0.5 px-1.5 rounded bg-emerald-600 text-white text-[10px]">
                        {mode === "today" && col.field && latest[col.field] != null
                          ? fmtVal(latest[col.field])
                          : "—"}
                      </span>
                    </div>
                  ) : (
                    col.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {HOURS.map((hour) => {
              const isOpen = expanded.has(hour);
              const hourData = hoursByIndex[hour];
              return (
                <React.Fragment key={hour}>
                  <DataRow
                    hour={hour}
                    onClick={() => toggleHour(hour)}
                    expanded={isOpen}
                    values={hourData}
                    isCurrent={hour === currentHour}
                    showHighlights={showHighlights}
                    heatRange={heatRange}
                    darkMode={darkMode}
                    nextTraded={nextTraded}
                  />
                  {isOpen
                    ? QUARTERS.map((q) => (
                        <DataRow
                          key={`${hour}-${q}`}
                          hour={hour}
                          quarter={q}
                          values={hourData?.quarters?.[q - 1]}
                          showHighlights={showHighlights}
                          heatRange={heatRange}
                          darkMode={darkMode}
                          nextTraded={nextTraded}
                        />
                      ))
                    : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* BPKD & PKD publication browser (polls every 10s)                  */}
      {/* ---------------------------------------------------------------- */}
      <BpkdPkdBrowser
        deliveryDate={resolvedDate}
        bpkdSelected={bpkdSelected}
        pkdSelected={pkdSelected}
        onSetBpkd={setBpkdSelected}
        onSetPkd={setPkdSelected}
      />
    </div>
  );
};

export default Dashboard;
